import type { Direction, GameState, Tile, TileAnimation } from './types';

// ── Constants ──────────────────────────────────────────────────────────────────

export const GRID      = 4;
const WIN_VALUE        = 2048;
const TILES_PER_SPAWN  = 1;
const PROB_4           = 0.25;  // 25% chance of spawning a 4, 75% a 2

// ── ID counter ────────────────────────────────────────────────────────────────

let nextId = 1;

// ── Public API ────────────────────────────────────────────────────────────────

export function createInitialState(bestScore: number): GameState {
  const tiles = spawnTiles([], TILES_PER_SPAWN);
  const animations: TileAnimation[] = tiles.map(t => ({
    tileId:  t.id,
    fromRow: t.row, fromCol: t.col,
    toRow:   t.row, toCol:   t.col,
    type:    'spawn' as const,
  }));
  return {
    tiles,
    score:       0,
    bestScore,
    status:      'playing',
    animations,
    animStartMs: 0,  // stamped by main.ts
  };
}

// Returns same state reference if the move is a no-op (nothing moved).
export function applyMove(state: GameState, dir: Direction): GameState {
  if (state.status !== 'playing') return state;

  // ── Build 5×5 lookup grid ────────────────────────────────────────────────
  const grid: (Tile | null)[][] = Array.from({ length: GRID }, () =>
    Array<Tile | null>(GRID).fill(null)
  );
  for (const t of state.tiles) grid[t.row][t.col] = t;

  // ── Process each line in slide direction ─────────────────────────────────
  const animations: TileAnimation[] = [];
  const newTiles:   Tile[]          = [];
  let   scoreDelta = 0;

  for (const line of buildLines(dir)) {
    // Extract tiles in slide order (null slots removed)
    const lineTiles = line
      .map(cell => grid[cell.row][cell.col])
      .filter((t): t is Tile => t !== null);

    const values    = lineTiles.map(t => t.value);
    const { result, mergeAt } = compactLine(values);

    // Map compacted results back to cell positions and build tile/animation lists
    let srcIdx = 0;
    for (let destIdx = 0; destIdx < GRID; destIdx++) {
      if (result[destIdx] === 0) continue;

      const destCell  = line[destIdx];
      const isMerge   = mergeAt.includes(destIdx);

      if (isMerge) {
        const src1 = lineTiles[srcIdx++];
        const src2 = lineTiles[srcIdx++];
        const mergedValue = result[destIdx];
        scoreDelta += mergedValue;

        // Surviving tile keeps src1's id with the merged value
        newTiles.push({ id: src1.id, value: mergedValue, row: destCell.row, col: destCell.col });

        // src1 slides and receives the pop animation
        animations.push({
          tileId:  src1.id,
          fromRow: src1.row, fromCol: src1.col,
          toRow:   destCell.row, toCol: destCell.col,
          type:    'merge-pop',
        });

        // src2 is a ghost — it slides to the same destination then vanishes
        animations.push({
          tileId:     src2.id,
          fromRow:    src2.row, fromCol: src2.col,
          toRow:      destCell.row, toCol: destCell.col,
          type:       'merge-ghost',
          ghostValue: src2.value,
        });
      } else {
        const src = lineTiles[srcIdx++];
        newTiles.push({ ...src, row: destCell.row, col: destCell.col });

        if (src.row !== destCell.row || src.col !== destCell.col) {
          animations.push({
            tileId:  src.id,
            fromRow: src.row, fromCol: src.col,
            toRow:   destCell.row, toCol: destCell.col,
            type:    'slide',
          });
        }
      }
    }
  }

  // If no tile moved or merged the move is a no-op
  if (animations.length === 0) return state;

  // ── Spawn 1 new tile ──────────────────────────────────────────────────────
  const emptyCount = GRID * GRID - newTiles.length;

  // If no room to spawn and no moves possible, lose.
  if (emptyCount === 0 && isGameOver(newTiles)) {
    const newScore = state.score + scoreDelta;
    return {
      tiles:       newTiles,
      score:       newScore,
      bestScore:   Math.max(state.bestScore, newScore),
      status:      'lost',
      animations,
      animStartMs: 0,
    };
  }

  const afterSpawn = spawnTiles(newTiles, TILES_PER_SPAWN);
  const prevIds    = new Set(newTiles.map(t => t.id));
  const spawnAnims: TileAnimation[] = afterSpawn
    .filter(t => !prevIds.has(t.id))
    .map(t => ({
      tileId:  t.id,
      fromRow: t.row, fromCol: t.col,
      toRow:   t.row, toCol:   t.col,
      type:    'spawn' as const,
    }));

  const newScore = state.score + scoreDelta;
  const hasWon   = afterSpawn.some(t => t.value >= WIN_VALUE);

  // Check terminal state: board full AND no adjacent merges possible.
  // This fires the turn AFTER the last empty cell is filled.
  const lost = !hasWon && isGameOver(afterSpawn);

  return {
    tiles:       afterSpawn,
    score:       newScore,
    bestScore:   Math.max(state.bestScore, newScore),
    status:      hasWon ? 'won' : lost ? 'lost' : 'playing',
    animations:  [...animations, ...spawnAnims],
    animStartMs: 0,
  };
}

// ── Internal helpers ──────────────────────────────────────────────────────────

interface Cell { row: number; col: number; }

// Returns true when the board is in a terminal (no-moves) state:
// all cells occupied AND no adjacent tiles share the same value.
function isGameOver(tiles: Tile[]): boolean {
  if (tiles.length < GRID * GRID) return false;  // empty cells still exist

  const grid: number[][] = Array.from({ length: GRID }, () =>
    Array<number>(GRID).fill(0)
  );
  for (const t of tiles) grid[t.row][t.col] = t.value;

  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const v = grid[r][c];
      if (c + 1 < GRID && grid[r][c + 1] === v) return false;  // horizontal pair
      if (r + 1 < GRID && grid[r + 1][c] === v) return false;  // vertical pair
    }
  }

  return true;  // no merges possible anywhere → game over
}

// Compact a single line: filter zeros, merge adjacent equal pairs, pad to GRID.
// mergeAt lists the output indices where a merge result was placed.
export function compactLine(values: number[]): { result: number[]; mergeAt: number[] } {
  const packed  = values.filter(v => v !== 0);
  const out:     number[] = [];
  const mergeAt: number[] = [];
  let i = 0;

  while (i < packed.length) {
    if (i + 1 < packed.length && packed[i] === packed[i + 1]) {
      mergeAt.push(out.length);
      out.push(packed[i] * 2);
      i += 2;
    } else {
      out.push(packed[i]);
      i++;
    }
  }

  while (out.length < GRID) out.push(0);
  return { result: out, mergeAt };
}

// Build 5 ordered lines of cells in the slide direction.
// Each line lists cells front-to-back in the direction of movement.
function buildLines(dir: Direction): Cell[][] {
  const lines: Cell[][] = [];

  if (dir === 'left') {
    for (let r = 0; r < GRID; r++) {
      lines.push(Array.from({ length: GRID }, (_, c) => ({ row: r, col: c })));
    }
  } else if (dir === 'right') {
    for (let r = 0; r < GRID; r++) {
      lines.push(Array.from({ length: GRID }, (_, c) => ({ row: r, col: GRID - 1 - c })));
    }
  } else if (dir === 'up') {
    for (let c = 0; c < GRID; c++) {
      lines.push(Array.from({ length: GRID }, (_, r) => ({ row: r, col: c })));
    }
  } else {
    // down
    for (let c = 0; c < GRID; c++) {
      lines.push(Array.from({ length: GRID }, (_, r) => ({ row: GRID - 1 - r, col: c })));
    }
  }

  return lines;
}

// Add `count` tiles to existing array, placed in random empty cells.
function spawnTiles(existing: Tile[], count: number): Tile[] {
  const occupied = new Set(existing.map(t => `${t.row},${t.col}`));
  const empties: Cell[] = [];

  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      if (!occupied.has(`${r},${c}`)) empties.push({ row: r, col: c });
    }
  }

  // Fisher-Yates shuffle, take first `count` cells
  for (let i = empties.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [empties[i], empties[j]] = [empties[j], empties[i]];
  }

  const chosen = empties.slice(0, count);
  const spawned: Tile[] = chosen.map(cell => ({
    id:    nextId++,
    value: Math.random() < PROB_4 ? 4 : 2,
    row:   cell.row,
    col:   cell.col,
  }));

  return [...existing, ...spawned];
}
